import type { DealChain } from './dealChain';
import type { ApiChainToken } from 'src/types/chain';
import type { ApiDealStepEvent } from 'src/types/dealStep';
import type { ApiRentalDealItem, ApiRentalContractState } from 'src/types/rental';
import type { TrackerStep, TrackerNote, TrackerHeadline, DealTrackerModel } from './dealTracker';

import { DealStep } from 'src/types/dealStep';
import { RentalDealStatus } from 'src/types/rental';

import { formatDateTime } from './date';
import { withAmount, trackerAmount } from './dealTracker';
import { supportsSplitProposals, INSPECTION_LOCK_SECONDS } from './rentalActions';

/**
 * The rental tracker (contracts v3, UI part §3) — the same postal-tracker shape the delivery deal
 * uses, over the rental's own story: booked → paid → ready → running → returned → deposit settled.
 *
 * Two things make it different from its delivery twin, and both are about not lying:
 *
 * 1. **A rental moves money without moving status.** A charged overdue day, a deposit top-up and
 *    an approved extension all leave the deal `active`. So the money notes are not decoration
 *    here — they are the only place those amounts are ever shown as events, with the transaction
 *    that carried each one.
 * 2. **Two generations of contract sit behind one screen.** The TRON deals (generation ≤ 3) are
 *    polled by a service that harvests no events at all, so their timeline carries only the
 *    platform steps. A milestone therefore counts as done when its event exists **or** when the
 *    deal's own mirrored flags prove it happened — and in the second case it carries no time,
 *    because we genuinely do not know when. An invented timestamp would be worse than a missing
 *    one; a step shown as un-started on a rental the renter is driving right now would be worse
 *    than both.
 */

interface RentalMilestone {
  key: string;
  title: string;
  description: string;
  /** The event that stamps this milestone with its time and transaction. */
  step: DealStep;
  /** What the mirror alone proves, for the chains whose poller records no events. */
  reachedByState: (state: ApiRentalContractState | null) => boolean;
  /** Steps whose notes belong under this milestone once it has been reached. */
  noteSteps?: DealStep[];
}

/** Anything that happened after the car went back: the settlement of the deposit, however it ended. */
const SETTLEMENT_STEPS: DealStep[] = [
  DealStep.RentalDepositReleased,
  DealStep.RentalDefectApproved,
  DealStep.RentalDisputeResolved,
  DealStep.RentalStaleDepositClaimed,
];

const MILESTONES: RentalMilestone[] = [
  {
    key: 'booked',
    title: 'Booked',
    description: 'You booked the car for these dates.',
    step: DealStep.RentalBookingCreated,
    // The booking exists because the deal exists — there is nothing on-chain to wait for.
    reachedByState: () => true,
  },
  {
    key: 'paid',
    title: 'Payment secured',
    description: 'Your rental, deposit and platform fee are held by the contract.',
    step: DealStep.RentalPaid,
    reachedByState: (state) => !!state?.isPaid,
  },
  {
    key: 'ready',
    title: 'Ready for pickup',
    description: 'The car is at the handover point and waiting for you.',
    step: DealStep.RentalReadyForPickup,
    reachedByState: (state) => !!state?.isReadyForPickup,
    noteSteps: [DealStep.RentalPickupConfirmed],
  },
  {
    key: 'started',
    title: 'Rental started',
    description: 'You accepted the car and the rental period began.',
    step: DealStep.RentalStarted,
    reachedByState: (state) => !!state && (state.isActive || state.isReturned || state.isInspection),
    // Everything that happens while the car is out: the days past the end date, the money that
    // paid for them, and the extensions that moved the end date itself.
    noteSteps: [
      DealStep.RentalOverdueCharged,
      DealStep.RentalDepositToppedUp,
      DealStep.RentalExtensionRequested,
      DealStep.RentalExtensionDeclined,
      DealStep.RentalExtended,
    ],
  },
  {
    key: 'returned',
    title: 'Car returned',
    description: 'You returned the car and the dealer has seven days to inspect it.',
    step: DealStep.RentalVehicleReturned,
    reachedByState: (state) => !!state && (state.isReturned || state.isInspection),
    noteSteps: [DealStep.RentalDefectReported],
  },
  {
    key: 'settled',
    title: 'Deposit settled',
    description: 'The rental is closed and the deposit has been paid out.',
    step: DealStep.RentalDepositReleased,
    reachedByState: (state) => !!state?.isCompleted,
    noteSteps: SETTLEMENT_STEPS,
  },
];

/**
 * What has to happen for **this** milestone, and who has to do it. Keyed by the milestone that has
 * not happened yet, so the line always describes what is still owed rather than what is done.
 */
const WAITING_FOR: Record<string, string> = {
  booked: 'Creating your booking',
  paid: 'Waiting for your payment',
  ready: 'Waiting for the dealer to confirm the car is ready for you',
  started: 'Waiting for you to accept the car',
  returned: 'The rental is running — waiting for you to return the car',
  settled: 'Waiting for the deposit to be released',
};

/**
 * The ending that replaced the happy path, or nothing when the rental is still on it.
 *
 * `cancelled` covers two different stories and they must not share one sentence: a paid booking
 * that was unwound (money came back) and a booking that expired before it was ever paid (no money
 * existed). Telling an unpaid renter their deposit was returned invites a support ticket about a
 * refund that was never owed.
 */
function endingOf(rental: ApiRentalDealItem): { title: string; description: string } | null {
  if (rental.status !== RentalDealStatus.CANCELLED) {
    return null;
  }

  const paid = !!rental.rentalContractState?.isPaid;

  return paid
    ? {
        title: 'Cancelled — refund issued',
        description:
          'The booking was cancelled before the car changed hands; the rental and the deposit went back to your wallet. The platform fee is not refunded.',
      }
    : {
        title: 'Booking cancelled',
        description: 'The booking was cancelled before it was paid, and its dates went back on sale. No money moved.',
      };
}

const HEADLINES: Record<RentalDealStatus, { status: string; tone: TrackerHeadline['tone'] }> = {
  [RentalDealStatus.CREATED]: { status: 'Ready to pay', tone: 'progress' },
  [RentalDealStatus.DEPLOY_INITIATED]: { status: 'Ready to pay', tone: 'progress' },
  [RentalDealStatus.DEPLOY_CONFIRMED]: { status: 'Ready to pay', tone: 'progress' },
  [RentalDealStatus.DEPLOY_FAILED]: { status: 'Ready to pay', tone: 'progress' },
  [RentalDealStatus.INITIALIZED]: { status: 'Ready to pay', tone: 'progress' },
  [RentalDealStatus.PAID]: { status: 'Payment secured in escrow', tone: 'success' },
  [RentalDealStatus.PICKED_UP]: { status: 'On its way to the handover point', tone: 'progress' },
  [RentalDealStatus.READY_FOR_PICKUP]: { status: 'The car is ready for you', tone: 'success' },
  [RentalDealStatus.ACTIVE]: { status: 'Rental running', tone: 'success' },
  [RentalDealStatus.OVERDUE]: { status: 'Past the end date', tone: 'danger' },
  [RentalDealStatus.DEPOSIT_EXHAUSTED]: { status: 'Past the end date — deposit exhausted', tone: 'danger' },
  [RentalDealStatus.RETURNED]: { status: 'Returned — inspection running', tone: 'progress' },
  [RentalDealStatus.DEFECTED]: { status: 'The dealer reported a defect', tone: 'danger' },
  [RentalDealStatus.DISPUTED]: { status: 'Deposit dispute', tone: 'danger' },
  [RentalDealStatus.COMPLETED]: { status: 'Completed', tone: 'success' },
  [RentalDealStatus.CANCELLED]: { status: 'Cancelled — refund issued', tone: 'neutral' },
};

const first = (events: ApiDealStepEvent[], step: DealStep): ApiDealStepEvent | undefined =>
  events.find((event) => event.step === step);

/** Seconds on the wire → an ISO instant, or null for the contract's own "not set" zero. */
const fromUnix = (value: string | null | undefined): string | null => {
  const seconds = Number(value ?? 0);

  return Number.isFinite(seconds) && seconds > 0 ? new Date(seconds * 1000).toISOString() : null;
};

/** A base-unit amount from an event's details, as an integer. Anything unreadable counts as zero. */
const toBaseUnits = (value: unknown): bigint => {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return 0n;
  }

  return BigInt(value);
};

const days = (seconds: unknown): string | null => {
  const value = Number(seconds ?? 0);

  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }

  const count = Math.round(value / 86_400);

  return `${count} day${count === 1 ? '' : 's'}`;
};

/**
 * "the dealer kept 200 EURC" — for lines whose sentence already leads into the amount, where the
 * dash `withAmount` inserts would read as a second one in the same line.
 */
const reading = (prefix: string, amount: string | null): string | null => (amount ? `${prefix} ${amount}` : null);

/** The money and the meaning of one rental event, read out of its own `details`. */
function notesOf(event: ApiDealStepEvent, token: ApiChainToken | null): TrackerNote[] {
  const details = event.details ?? {};
  const notes: TrackerNote[] = [];
  const add = (label: string | null) => {
    if (label) notes.push({ label, at: event.occurredAt, txHash: event.txHash });
  };
  const amount = (value: unknown) => trackerAmount(value, token);

  switch (event.step) {
    case DealStep.RentalPaid:
      add(withAmount('Paid into escrow', amount(details.totalPaid)));
      add(withAmount('Platform fee taken', amount(details.feePaid)));
      break;
    case DealStep.RentalPickupConfirmed:
      add('The logist collected the car from the dealer');
      break;
    case DealStep.RentalStarted:
      add(withAmount('Rental released to the dealer', amount(details.rentalPaid)));
      break;
    case DealStep.RentalOverdueCharged:
      add(withAmount('Charged for time past the end date', amount(details.chargeAmount)));
      break;
    case DealStep.RentalDepositToppedUp:
      add(withAmount('Deposit topped up', amount(details.amount)));
      add(withAmount('Of that, paid to the dealer for overdue time', amount(details.debtPaid)));
      break;
    case DealStep.RentalExtensionRequested:
      add(withAmount('Extension requested', days(details.requestedPeriod)));
      break;
    case DealStep.RentalExtensionDeclined:
      add('Extension declined by the dealer — nothing was charged');
      break;
    case DealStep.RentalExtended: {
      // The renter was charged the rental and its fee in one transfer, so one line says so —
      // added as base-unit integers, because that is what the contract emitted.
      const extra = toBaseUnits(details.extraRental) + toBaseUnits(details.extraFee);

      add(withAmount('Rental extended', days(details.addedPeriod)));
      add(withAmount('Charged for the extension', extra > 0n ? amount(extra.toString()) : null));
      break;
    }
    case DealStep.RentalDefectReported:
      add(withAmount('The dealer claimed a defect', amount(details.amount)));
      break;
    case DealStep.RentalDefectApproved:
      add(reading('You accepted the claim — the dealer kept', amount(details.rentierAmount)));
      add(withAmount('Returned to you', amount(details.renterAmount)));
      break;
    case DealStep.RentalDefectDisputed:
      add('You disputed the claim — the deposit is held until it is settled');
      break;
    case DealStep.RentalSplitProposed:
      add(reading('Split proposed — the dealer would keep', amount(details.rentierAmount)));
      break;
    case DealStep.RentalDisputeResolved:
      add(reading('Split executed — the dealer kept', amount(details.rentierAmount)));
      add(withAmount('Returned to you', amount(details.renterAmount)));
      break;
    case DealStep.RentalDepositReleased:
      add(withAmount('Deposit returned', amount(details.amount)));
      break;
    case DealStep.RentalStaleDepositClaimed:
      add(withAmount('Deposit claimed after the 14-day timeout', amount(details.amount)));
      break;
    case DealStep.RentalCancelled:
    case DealStep.RentalExpiredBookingCancelled:
      add(withAmount('Refund issued', amount(details.refundAmount)));
      break;
    default:
      break;
  }

  return notes;
}

/** The notes of every event belonging to a milestone, oldest first. */
const notesFor = (
  milestone: RentalMilestone,
  events: ApiDealStepEvent[],
  token: ApiChainToken | null
): TrackerNote[] =>
  events
    .filter((event) => event.step === milestone.step || milestone.noteSteps?.includes(event.step))
    .flatMap((event) => notesOf(event, token));

/**
 * The six milestones, each carrying its own time and whatever money it moved.
 *
 * A milestone is `done` when its event exists (then it is stamped) or when the mirror proves it
 * (then it is not). The first milestone that is neither is where the rental stands right now —
 * unless the rental ended off the happy path, in which case there is no current step at all.
 */
function buildSteps(
  rental: ApiRentalDealItem,
  events: ApiDealStepEvent[],
  token: ApiChainToken | null
): TrackerStep[] {
  const state = rental.rentalContractState ?? null;
  const exception = endingOf(rental);
  const steps: TrackerStep[] = [];
  let currentTaken = false;

  MILESTONES.forEach((milestone) => {
    // A settled deposit can end in four different ways; whichever event exists stamps the step.
    const stamp =
      milestone.key === 'settled'
        ? SETTLEMENT_STEPS.map((step) => first(events, step)).find(Boolean)
        : first(events, milestone.step);
    // A cancelled rental is `isCompleted` on-chain — the unwind sets it — so the flags alone would
    // tick "Deposit settled" on a booking whose deposit was never at risk. The ending step below
    // tells that story; this one stays untouched unless a settlement event really happened.
    const reachedByState = milestone.key === 'settled' && exception ? false : milestone.reachedByState(state);
    const reached = !!stamp || reachedByState;

    if (reached) {
      steps.push({
        key: milestone.key,
        title: milestone.title,
        description: milestone.description,
        state: 'done',
        at: stamp?.occurredAt ?? null,
        txHash: stamp?.txHash ?? null,
        notes: notesFor(milestone, events, token),
      });

      return;
    }

    const isCurrent = !currentTaken && !exception;
    currentTaken = currentTaken || isCurrent;

    steps.push({
      key: milestone.key,
      title: milestone.title,
      description: milestone.description,
      state: isCurrent ? 'current' : 'future',
      at: null,
      txHash: null,
      waitingFor: isCurrent ? WAITING_FOR[milestone.key] : undefined,
      // A step we are still waiting for can already have events under it — the logist has the car
      // but the dealer has not confirmed it is ready — and dropping them would hide a fact that
      // carries its own timestamp. A genuinely future milestone has none, so this is empty there.
      notes: notesFor(milestone, events, token),
    });
  });

  return steps;
}

/** The dispute and the ending — the steps that exist only when something went sideways. */
function buildExceptionSteps(
  rental: ApiRentalDealItem,
  events: ApiDealStepEvent[],
  token: ApiChainToken | null
): TrackerStep[] {
  const state = rental.rentalContractState ?? null;
  const steps: TrackerStep[] = [];
  const disputed = !!state?.isDisputed;
  const raised = first(events, DealStep.RentalDefectDisputed);

  if (disputed || raised) {
    const closing = first(events, DealStep.RentalDisputeResolved) ?? first(events, DealStep.RentalStaleDepositClaimed);
    const settled = !!closing || !!state?.isCompleted;
    // How a disputed deposit is settled depends on the contract's generation, and the difference
    // is the whole point of v3: the older clones let the platform rule alone, the new ones need
    // two of the three parties to name the same number.
    const byProposals = supportsSplitProposals(state);

    steps.push({
      key: 'dispute',
      title: settled ? 'Deposit dispute settled' : 'Deposit dispute',
      description: settled
        ? 'The deposit was split and the rental closed.'
        : byProposals
          ? 'The deposit is held until two of the three parties propose the same split.'
          : 'The deposit is held until GAC settles the claim.',
      state: 'exception',
      at: raised?.occurredAt ?? null,
      txHash: raised?.txHash ?? null,
      waitingFor: settled
        ? undefined
        : byProposals
          ? 'Waiting for two matching proposals — you, the dealer and GAC'
          : 'Waiting for GAC to settle the claim',
      notes: events
        .filter(
          (event) =>
            event.step === DealStep.RentalSplitProposed ||
            event.step === DealStep.RentalDisputeResolved ||
            event.step === DealStep.RentalStaleDepositClaimed
        )
        .flatMap((event) => notesOf(event, token)),
    });
  }

  const exception = endingOf(rental);

  if (exception) {
    const ending =
      first(events, DealStep.RentalCancelled) ??
      first(events, DealStep.RentalExpiredBookingCancelled) ??
      first(events, DealStep.RentalBookingExpired);

    steps.push({
      key: 'ending',
      title: exception.title,
      description: exception.description,
      state: 'exception',
      at: ending?.occurredAt ?? null,
      txHash: ending?.txHash ?? null,
      notes: ending ? notesOf(ending, token) : [],
    });
  }

  return steps;
}

/**
 * The header's second line: the date the renter is actually waiting on.
 *
 * Every one of them is a fact somebody recorded — a deadline the contract holds, an end date it
 * moved, the moment an inspection window opened — never a guess derived from the status.
 */
function buildDetail(rental: ApiRentalDealItem, nowMs: number): string | null {
  const state = rental.rentalContractState ?? null;

  if (state?.isDisputed && !state.isCompleted) {
    return rental.staleClaimAvailableAt
      ? `If nobody agrees, you can claim the remaining deposit from ${formatDateTime(rental.staleClaimAvailableAt)}`
      : 'Waiting for two matching proposals';
  }

  if (state?.isDefected && !state.isCompleted) {
    return 'Waiting for your answer to the defect claim';
  }

  if (state && !state.isPaid && !state.isCompleted) {
    const deadline = fromUnix(state.payDeadline);

    return deadline ? `Pay before ${formatDateTime(deadline)}` : null;
  }

  if (state?.isInspection && !state.isCompleted) {
    const unlock = Number(state.inspectionStart) + INSPECTION_LOCK_SECONDS;

    return unlock > 0 ? `Deposit unlocks ${formatDateTime(unlock * 1000)}` : null;
  }

  if (state?.isActive && !state.isReturned) {
    const end = fromUnix(state.rentalEnd);

    if (!end) {
      return null;
    }

    return new Date(end).getTime() > nowMs
      ? `Ends ${formatDateTime(end)}`
      : `Ended ${formatDateTime(end)} — every started day is charged from your deposit`;
  }

  if (state?.isPaid && !state.isActive && !state.isCompleted) {
    return state.isReadyForPickup
      ? 'Waiting for you to accept the car'
      : 'Waiting for the dealer to hand the car over';
  }

  return null;
}

export function buildRentalTracker(
  rental: ApiRentalDealItem,
  chain: DealChain | null,
  nowMs: number = Date.now()
): DealTrackerModel {
  const token = chain?.token ?? null;
  // api returns them oldest-first; sorting here keeps the tracker honest against any order.
  const events = [...(rental.stepEvents ?? [])].sort(
    (left, right) => new Date(left.occurredAt).getTime() - new Date(right.occurredAt).getTime()
  );

  // An ending speaks for the headline too. `cancelled` is one status over two different stories,
  // and reading the headline off a status map while the step below reads it off the payment let a
  // booking that was never paid announce "Cancelled — refund issued" above "No money moved".
  const ending = endingOf(rental);

  return {
    headline: {
      ...HEADLINES[rental.status],
      ...(ending ? { status: ending.title } : {}),
      detail: buildDetail(rental, nowMs),
    },
    steps: [...buildSteps(rental, events, token), ...buildExceptionSteps(rental, events, token)],
  };
}
